import { Component, inject } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { NgGlyph } from "@ng-icons/core";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { AutocompleteOption } from "../../../../Common/Components/Autocomplete/Type/AutocompleteTypes";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { AppSummaryPanelComponent, SummaryPanelRow } from "../../../../Common/Components/SummaryPanel/SummaryPanelComponent";
import { RequestApi } from "../../Api/RequestApi";
import { RequestEffects } from "../../State/RequestEffects";
import { CreateRequestRequest, RequestUpsertItem } from "../../Dto/CreateRequestRequest";
import { RequestLinePreviewState, RequestLineVisualState, RequestStockPreviewItem } from "../../Dto/RequestStockPreview";
import { TeamLeaderApi } from "../../../TeamLeader/Api/TeamLeaderApi";
import { TeamLeaderListItem } from "../../../TeamLeader/Dto/TeamLeaderModel";
import { WarehouseApi } from "../../../Warehouse/Api/WarehouseApi";
import { MaterialApi } from "../../../Material/Api/MaterialApi";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { QtyInputSelectZeroDirective } from "../../../../Common/Components/QtyInput/QtyInputSelectZeroDirective";
import {
  MaterialAutocompleteOption,
  mergeMaterialAutocompleteOptions,
  toMaterialAutocompleteOption,
} from "../../../../Common/Utility/UtilityMaterialSequence";

@Component({
  selector: "app-request-create",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AppButtonComponent,
    AppCardComponent,
    AutocompleteComponent,
    RichTextEditorComponent,
    AppSummaryPanelComponent,
    QtyInputSelectZeroDirective,
    NgGlyph,
  ],
  templateUrl: "./RequestCreatePage.html",
  styleUrl: "./RequestCreatePage.scss",
})
export class RequestCreatePage {
  private readonly fb = inject(FormBuilder);
  private readonly requestApi = inject(RequestApi);
  private readonly requestEffects = inject(RequestEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly router = inject(Router);
  private readonly teamLeaderApi = inject(TeamLeaderApi);
  private readonly warehouseApi = inject(WarehouseApi);
  private readonly materialApi = inject(MaterialApi);
  private previewRequestVersion = 0;

  isSaving = false;
  teamLeaderOptions: AutocompleteOption[] = [];
  partnerOptions: AutocompleteOption[] = [];
  warehouseOptions: AutocompleteOption[] = [];
  materialSearchOptions: MaterialAutocompleteOption[] = [];
  materialOptionsByIndex: MaterialAutocompleteOption[][] = [[]];
  selectedTeamLeader: TeamLeaderListItem | null = null;
  linePreviewStates: RequestLinePreviewState[] = [this.createLinePreviewState()];
  hasDuplicateMaterials = false;

  form = this.fb.group({
    teamLeaderId: ["", [Validators.required]],
    partnerId: ["", [Validators.required]],
    warehouseId: ["", [Validators.required]],
    note: [""],
    items: this.fb.array([this.createItemForm()]),
  });

  get items(): FormArray {
    return this.form.get("items") as FormArray;
  }

  get canSelectPartner(): boolean {
    return !!this.form.get("teamLeaderId")?.value;
  }

  get partnerHelperText(): string {
    return (() => {
      if (this.canSelectPartner) {
        return "Showing partners assigned to this team leader.";
      }
      return "Choose a team leader first.";
    })();
  }

  get totalRequestedQty(): number {
    return this.items.controls.reduce((total, control) => total + Number(control.get("qty")?.value ?? 0), 0);
  }

  get readyLineCount(): number {
    return this.items.controls.filter((_, index) => this.getLineVisualState(index) === "ready").length;
  }

  get constrainedLineCount(): number {
    return this.items.controls.filter((_, index) => {
      const state = this.getLineVisualState(index);
      return state === "warning" || state === "unavailable";
    }).length;
  }

  get reviewRows(): SummaryPanelRow[] {
    return [
      { label: "Mode", value: "Create Request" },
      { label: "Team Leader", value: this.selectedTeamLeader?.name || "Not selected" },
      { label: "Partner", value: this.getSelectedPartnerLabel() },
      { label: "Warehouse", value: this.getSelectedWarehouseLabel() },
      { label: "Lines", value: this.items.length },
      { label: "Total Qty", value: this.totalRequestedQty },
      { label: "Lines Ready", value: this.readyLineCount },
      { label: "Needs Review", value: this.constrainedLineCount },
    ];
  }

  private createItemForm() {
    return this.fb.group({
      materialId: ["", [Validators.required]],
      qty: [0, [Validators.required, Validators.min(1)]],
    });
  }

  private createLinePreviewState(materialId = "", preview: RequestStockPreviewItem | null = null): RequestLinePreviewState {
    return {
      materialId,
      preview,
      status: (() => {
        if (preview) {
          return this.getLoadedPreviewStatus(preview);
        }
        return "idle";
      })(),
    };
  }

  addItem(): void {
    this.items.push(this.createItemForm());
    this.materialOptionsByIndex.push(this.mergeMaterialOptions([], this.materialSearchOptions));
    this.linePreviewStates.push(this.createLinePreviewState());
    this.updateDuplicateState();
  }

  removeItem(index: number): void {
    if (this.items.length <= 1) {
      return;
    }

    this.items.removeAt(index);
    this.materialOptionsByIndex.splice(index, 1);
    this.linePreviewStates.splice(index, 1);
    this.updateDuplicateState();
    void this.refreshStockPreview();
  }

  async save(): Promise<void> {
    this.updateDuplicateState();

    if (this.hasDuplicateMaterials) {
      this.toastEffects.show("Each request line must use a different material.", "error");
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastEffects.show("Please fill in all required request details.", "error");
      return;
    }

    const payload: CreateRequestRequest = {
      teamLeaderId: (this.form.value.teamLeaderId ?? "").trim(),
      partnerId: (this.form.value.partnerId ?? "").trim(),
      warehouseId: (this.form.value.warehouseId ?? "").trim(),
      note: (this.form.value.note ?? "").trim(),
      items: (this.items.value as RequestUpsertItem[]).map((item) => ({
        materialId: item.materialId,
        qty: Number(item.qty),
      })),
    };

    this.isSaving = true;
    try {
      await this.requestEffects.createRequest(payload);
      this.toastEffects.show("Request created successfully.", "success");
      await this.router.navigate(["/requests"]);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create request."), "error");
    } finally {
      this.isSaving = false;
    }
  }

  goBack(): void {
    this.router.navigate(["/requests"]).catch(() => undefined);
  }

  async searchTeamLeaders(term: string): Promise<void> {
    const response = await this.teamLeaderApi.searchTeamLeaders({ search: term, page: 1, limit: 50 });
    this.teamLeaderOptions = response.data.map((item) => ({ value: item.id, label: item.name }));
  }

  searchPartners(term: string): void {
    const partners = this.selectedTeamLeader?.partners ?? [];
    const normalizedTerm = term.trim().toLowerCase();
    this.partnerOptions = partners
      .filter((partner) => !normalizedTerm || partner.name.toLowerCase().includes(normalizedTerm))
      .map((partner) => ({ value: partner.id, label: partner.name }));
  }

  async searchWarehouses(term: string): Promise<void> {
    const response = await this.warehouseApi.searchWarehouses({ search: term, page: 1, limit: 50 });
    const options = response.data.map((item) => ({ value: item.id, label: item.name }));
    this.warehouseOptions = this.mergeUniqueOptions(this.warehouseOptions, options);
  }

  async searchMaterials(term: string): Promise<void> {
    const response = await this.materialApi.searchMaterials({ search: term, page: 1, limit: 50 });
    this.materialSearchOptions = response.data.map((item) => toMaterialAutocompleteOption(item));
    this.materialOptionsByIndex = this.materialOptionsByIndex.map((existing) =>
      this.mergeMaterialOptions(existing, this.materialSearchOptions),
    );
  }

  async onTeamLeaderChange(value: string | number | (string | number)[]): Promise<void> {
    if (Array.isArray(value)) return;

    const nextTeamLeaderId = value.toString();
    this.form.patchValue({ teamLeaderId: nextTeamLeaderId });
    this.form.patchValue({ partnerId: "" });
    this.partnerOptions = [];

    if (!nextTeamLeaderId) {
      this.selectedTeamLeader = null;
      return;
    }

    const teamLeader = await this.teamLeaderApi.getTeamLeaderById(nextTeamLeaderId);
    this.selectedTeamLeader = teamLeader;
    this.teamLeaderOptions = this.mergeUniqueOptions(this.teamLeaderOptions, [
      { value: teamLeader?.id ?? nextTeamLeaderId, label: teamLeader?.name ?? nextTeamLeaderId },
    ]);
    this.searchPartners("");
  }

  onPartnerChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) return;
    this.form.patchValue({ partnerId: value.toString() });
  }

  onWarehouseChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) return;
    const warehouseId = value.toString();
    this.form.patchValue({ warehouseId: warehouseId });

    const selected = this.warehouseOptions.find((option) => option.value.toString() === warehouseId);
    if (selected) {
      this.warehouseOptions = this.mergeUniqueOptions(this.warehouseOptions, [selected]);
    }

    void this.refreshStockPreview();
  }

  onMaterialChange(index: number, value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) return;
    const materialId = value.toString();
    this.items.at(index).patchValue({ materialId });

    const selected =
      this.materialSearchOptions.find((option) => option.value.toString() === materialId) ??
      this.materialOptionsByIndex[index]?.find((option) => option.value.toString() === materialId);
    if (selected) {
      this.materialOptionsByIndex[index] = this.mergeMaterialOptions(this.materialOptionsByIndex[index] ?? [], [selected]);
    }

    this.updateDuplicateState();
    void this.refreshStockPreview();
  }

  onQtyInput(): void {
    this.updateDuplicateState();
  }

  getSelectedWarehouseLabel(): string {
    const warehouseId = this.form.get("warehouseId")?.value ?? "";
    return this.warehouseOptions.find((option) => option.value.toString() === warehouseId)?.label ?? "Not selected";
  }

  getSelectedPartnerLabel(): string {
    const partnerId = this.form.get("partnerId")?.value ?? "";
    return this.partnerOptions.find((option) => option.value.toString() === partnerId)?.label ?? "Not selected";
  }

  getItemForm(index: number): FormGroup {
    return this.items.at(index) as FormGroup;
  }

  getItemMaterialId(index: number): string {
    return (this.items.at(index).get("materialId")?.value ?? "").toString();
  }

  getItemQty(index: number): number {
    return Number(this.items.at(index).get("qty")?.value ?? 0);
  }

  getReviewMaterialLabel(index: number): string {
    const materialId = this.getItemMaterialId(index);
    return this.materialOptionsByIndex[index]?.find((option) => option.value.toString() === materialId)?.label ?? "Material pending";
  }

  getMaterialPreview(index: number): RequestStockPreviewItem | null {
    return this.linePreviewStates[index]?.preview ?? null;
  }

  getLineVisualState(index: number): RequestLineVisualState {
    const materialId = this.getItemMaterialId(index);
    if (!materialId || !(this.form.get("warehouseId")?.value ?? "")) {
      return "missing";
    }

    const lineState = this.linePreviewStates[index];
    if (!lineState || lineState.materialId !== materialId) {
      return "loading";
    }

    if (lineState.status === "loading") {
      return "loading";
    }

    const preview = lineState.preview;
    if (!preview || lineState.status === "unavailable" || this.isUnavailablePreview(preview)) {
      return "unavailable";
    }

    if (preview.available < this.getItemQty(index)) {
      return "warning";
    }

    return "ready";
  }

  getLineStatusLabel(index: number): string {
    switch (this.getLineVisualState(index)) {
      case "loading":
        return "Checking";
      case "ready":
        return "Ready";
      case "warning":
        return "Needs Review";
      case "unavailable":
        return "No Stock";
      default:
        return "Pending";
    }
  }

  getLineStatusMessage(index: number): string {
    switch (this.getLineVisualState(index)) {
      case "loading":
        return "Checking warehouse availability for this line.";
      case "ready":
        return "Enough available stock for this requested quantity.";
      case "warning":
        return "Requested quantity is above currently available stock in this warehouse.";
      case "unavailable":
        return "No stock found in this warehouse yet for this material.";
      default:
        return "Choose a warehouse and material to preview availability for this line.";
    }
  }

  isFirstPreviewLoad(): boolean {
    const hasAnyMaterial = this.items.controls.some((control) => (control.get("materialId")?.value ?? "").toString().trim());
    return hasAnyMaterial && this.linePreviewStates.every((state) => state.status === "loading" && !state.preview);
  }

  isDuplicateMaterial(materialId: string, index: number): boolean {
    if (!materialId) {
      return false;
    }

    return this.items.controls.some(
      (control, currentIndex) => currentIndex !== index && control.get("materialId")?.value === materialId,
    );
  }

  private updateDuplicateState(): void {
    const materialIds = this.items.controls
      .map((control) => (control.get("materialId")?.value ?? "").toString())
      .filter((materialId) => materialId.trim());

    this.hasDuplicateMaterials = new Set(materialIds).size !== materialIds.length;
  }

  private async refreshStockPreview(): Promise<void> {
    this.ensureLinePreviewStateLength();
    const warehouseId = (this.form.get("warehouseId")?.value ?? "").toString();
    const selectedMaterials = this.items.controls.map((control, index) => ({
      index,
      materialId: (control.get("materialId")?.value ?? "").toString().trim(),
    }));
    const materialIds = Array.from(new Set(selectedMaterials.map((entry) => entry.materialId).filter(Boolean)));

    if (!warehouseId || materialIds.length === 0) {
      this.previewRequestVersion += 1;
      this.linePreviewStates = selectedMaterials.map((entry) => this.createLinePreviewState(entry.materialId));
      return;
    }

    const requestVersion = ++this.previewRequestVersion;
    this.linePreviewStates = selectedMaterials.map((entry, index) => {
      const previous = this.linePreviewStates[index] ?? this.createLinePreviewState();
      if (!entry.materialId) {
        return this.createLinePreviewState();
      }

      const preview = (() => {
        if (previous.materialId === entry.materialId) {
          return previous.preview;
        }
        return null;
      })();
      return {
        materialId: entry.materialId,
        preview,
        status: "loading",
      };
    });

    try {
      const response = await this.requestApi.previewStock(warehouseId, materialIds);
      if (requestVersion !== this.previewRequestVersion) {
        return;
      }

      const previewByMaterialId = response.data.reduce<Record<string, RequestStockPreviewItem>>((accumulator, item) => {
        accumulator[item.materialId] = item;
        return accumulator;
      }, {});

      this.linePreviewStates = selectedMaterials.map((entry) => {
        if (!entry.materialId) {
          return this.createLinePreviewState();
        }

        const preview = previewByMaterialId[entry.materialId] ?? null;
        return {
          materialId: entry.materialId,
          preview,
          status: (() => {
            if (preview) {
              return this.getLoadedPreviewStatus(preview);
            }
            return "unavailable";
          })(),
        };
      });
    } catch {
      if (requestVersion !== this.previewRequestVersion) {
        return;
      }

      this.linePreviewStates = selectedMaterials.map((entry, index) => {
        const previous = this.linePreviewStates[index] ?? this.createLinePreviewState();
        if (!entry.materialId) {
          return this.createLinePreviewState();
        }

        return {
          materialId: entry.materialId,
          preview: (() => {
            if (previous.materialId === entry.materialId) {
              return previous.preview;
            }
            return null;
          })(),
          status: (() => {
            if (previous.preview) {
              return this.getLoadedPreviewStatus(previous.preview);
            }
            return "unavailable";
          })(),
        };
      });
    }
  }

  private ensureLinePreviewStateLength(): void {
    while (this.linePreviewStates.length < this.items.length) {
      this.linePreviewStates.push(this.createLinePreviewState());
    }

    if (this.linePreviewStates.length > this.items.length) {
      this.linePreviewStates = this.linePreviewStates.slice(0, this.items.length);
    }
  }

  private getLoadedPreviewStatus(preview: RequestStockPreviewItem): "ready" | "unavailable" {
    return (() => {
      if (this.isUnavailablePreview(preview)) {
        return "unavailable";
      }
      return "ready";
    })();
  }

  private isUnavailablePreview(preview: RequestStockPreviewItem): boolean {
    return preview.onHand === 0 && preview.reserved === 0 && preview.available === 0;
  }

  private mergeUniqueOptions(existing: AutocompleteOption[], incoming: AutocompleteOption[]): AutocompleteOption[] {
    const optionMap = new Map<string, AutocompleteOption>();
    [...existing, ...incoming].forEach((option) => {
      optionMap.set(option.value.toString(), option);
    });

    return Array.from(optionMap.values());
  }

  private mergeMaterialOptions(
    existing: MaterialAutocompleteOption[],
    incoming: MaterialAutocompleteOption[],
  ): MaterialAutocompleteOption[] {
    return mergeMaterialAutocompleteOptions(existing, incoming);
  }
}
